import { ExpandCircleDownIcon, ExpandCircleUpIcon } from "@src/assets";
import { Fees } from "@src/interfaces";
import { displayLessThan } from "@src/utils";
import { useState } from "react";
import { Skeleton } from "../Skeleton";
import { IconWithTooltip } from "../IconWithTooltip";

interface Props {
  gasFees?: Fees;
  isFetchingRoute: boolean;
  title?: React.ReactNode;
  feesErrorText?: string;
  feesErrorVisible?: boolean;
}

export const ExpandableFees = ({
  gasFees,
  isFetchingRoute,
  title,
  feesErrorText,
  feesErrorVisible,
}: Props) => {
  const [feesExpanded, setFeesExpanded] = useState(false);

  return (
    <div
      className={`bg-card_bg rounded-xl ${
        feesErrorVisible && "!bg-background_primary"
      }`}
    >
      <div
        className={`flex flex-col text-xs text-text_primary rounded-xl px-4 py-3 ${
          feesErrorVisible && "!bg-error_color/20"
        }`}
      >
        <div className="flex items-center justify-between gap-1">
          {title || <div>Fees included</div>}

          <div
            className="cursor-pointer"
            onClick={() => setFeesExpanded((prev) => !prev)}
          >
            {feesExpanded ? <ExpandCircleUpIcon /> : <ExpandCircleDownIcon />}
          </div>
        </div>
        {gasFees && (
          <div
            className={`transition-[max-height] duration-500 overflow-hidden ${
              feesExpanded ? "max-h-[100px]" : "max-h-0"
            }`}
          >
            {feesErrorVisible && (
              <p className="text-sm text-text_primary/60">{feesErrorText}</p>
            )}
            <div className="flex flex-col gap-1 mt-2 pt-2 border-t border-solid border-text_primary border-opacity-10">
              {Number(gasFees.onrampFee) !== 0 && (
                <div className="flex justify-between">
                  <div className="flex gap-1 justify-center items-center">
                    <IconWithTooltip
                      id="onramp-fee-tooltip"
                      tooltipText="Fee charged by the on-ramp provider for converting your funds into crypto."
                    />
                    <p>Onramp fee</p>
                  </div>
                  <div>{`${displayLessThan(gasFees.onrampFee, 0.01)} $`}</div>
                </div>
              )}
              <div className="flex justify-between">
                <div className="flex gap-1 justify-center items-center">
                  <IconWithTooltip
                    id="network-fee-tooltip"
                    tooltipText="The fee fluctuates with market activity, and you'll receive a cashback if it decreases during the transaction."
                  />
                  <p>Network fee</p>
                </div>
                <div className="flex flex-row gap-1">
                  {isFetchingRoute ? (
                    <Skeleton className="w-5 h-3" />
                  ) : (
                    `${displayLessThan(gasFees.networkFee, 0.01)}`
                  )}
                  {" $"}
                </div>
              </div>
              <div className="flex justify-between">
                <div className="flex gap-1 justify-center items-center">
                  <IconWithTooltip
                    id="platform-fee-tooltip"
                    tooltipText="Fee that is a percentage of the value of your transaction."
                  />
                  <p>Platform fee</p>
                </div>
                <div className="flex flex-row gap-1 justify-center items-center">
                  {isFetchingRoute ? (
                    <Skeleton className="w-5 h-3" />
                  ) : (
                    `${displayLessThan(gasFees.platformFee, 0.01)}`
                  )}
                  {" $"}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
